import React, { Fragment, useEffect, useState } from 'react'
import { superAxiosRequest } from 'axiosApi'
import { splitNumberDigits } from 'helpers/math'
import useTableFixedHeader from 'hooks/useTableFixedHeader'
import ContentHeader from 'components/UI/ContentHeader/ContentHeader'
import THead from 'components/ContentBox/Global/ReportTable/THead'
import Loader from 'components/ContentBox/Global/Loader/Loader'
import st from 'components/ContentBox/Global/ReportTable/Table.module.scss'
import { ExcelRowsParams, setCellParams } from 'components/utils/ExcelExport'
import ExportExcelButton from '../ExportExcelButton/ExportExcelButton'
import { observer } from 'mobx-react'
import useLocalStorageFilter from 'helpers/useLocalStorageFilter'
import AsideFilterStore from 'components/UI/AsideFilter/asideFilterStore'
import SearchBooks from 'components/Modules/Ebook/Books/components/SearchBooks/SearchBooks'
import FilterTextValues from 'components/UI/AsideFilter/FilterTextValues'
import AsideFilter from 'components/UI/AsideFilter/AsideFilter'
import useLocalization from 'hooks/useLocalization'
import { useHistory } from 'react-router-dom'

// Функция для форматирования числа с процентом
const formatNumberWithPercent = (value, baseValue) => {
  const num = Number(value)
  const base = Number(baseValue)
  if (base === 0) return splitNumberDigits(num)
  const percent = (num / base) * 100
  return `${splitNumberDigits(num)} (${percent.toFixed(1)}%)`
}

// Функция для расчета наполняемости студентов по отработанным часам
const calculateStudentCapacityByHours = (studentHours, teacherHours) => {
  const sHours = Number(studentHours) || 0
  const tHours = Number(teacherHours) || 0
  if (tHours === 0) return '0.00'
  return (sHours / tHours).toFixed(2)
}

// Функция для расчета средней загрузки учителя
const calculateTeacherAverageLoad = (teacherHours, teacherCount) => {
  const hours = Number(teacherHours) || 0
  const count = Number(teacherCount) || 0
  if (count === 0) return '0.00'
  return (hours / count).toFixed(2)
}

const ProfitabilityPrograms = () => {
  const [catalog, setCatalog] = useState({})
  const [filter, updateFilter] = useLocalStorageFilter()
  const {
    isOpenFilter,
    closeFilter,
    setTextValues,
    changeTextValue,
    filterTextValues,
  } = AsideFilterStore
  const history = useHistory()

  const {
    refWrapper,
    refSticky,
    refThead,
    data,
    setData,
    isLoading,
    setIsLoading,
    getTdClassNames,
  } = useTableFixedHeader()

  const { translate } = useLocalization({
    section: 'ebooks',
    subsection: 'ebooks_books',
    dict: {
      edit: 'Edit',
      copy: 'Copy',
      delete: 'Delete',
      save: 'Save',
      filter: 'Filter',
      company: 'Company',
      reset_all: 'Reset all',
      apply: 'Apply',
      search: 'Search',
      start_date: 'Start date',
      end_date: 'End date',
    },
  })

  const filterCnf = [
    {
      type: 'select-multi',
      label: 'Локация',
      nameState: 'location',
      placeholder: 'Локация',
      values:
        catalog.locations?.sort((a, b) => (a.name > b.name ? 1 : -1)) || [],
      option_value_key: 'id',
      option_label_key: 'name',
    },
    {
      type: 'date_day_picker',
      nameState: ['date_from', 'date_until'],
      label: 'Период',
    },
    {
      type: 'select-multi',
      label: 'Тип программы',
      placeholder: 'Тип программы',
      values: catalog.program_types || [],
      sort_key: 'name',
      nameState: 'program_type',
      is_search: true,
    },
    {
      type: 'type_selector',
      label: 'Формат',
      values: [
        { id: 2, name: 'Все группы' },
        { id: 1, name: 'Онлайн' },
        { id: 0, name: 'Офлайн' },
      ],
      nameState: 'online',
    },
  ]

  // Функция для обогащения данных локаций метаданными из catalog
  const enrichLocationsWithMetadata = (resultLocations, catalogLocations) => {
    if (!resultLocations || !catalogLocations) return resultLocations

    const catalogLocationsMap = new Map()
    catalogLocations.forEach((loc) => {
      catalogLocationsMap.set(loc.id, {
        class_rooms: loc.class_rooms,
        square_meters: loc.square_meters,
        name: loc.name,
      })
    })

    return resultLocations.map((location) => {
      const metadata = catalogLocationsMap.get(location.id)
      return {
        ...location,
        class_rooms: metadata?.class_rooms || 0,
        square_meters: metadata?.square_meters || 0,
        location_name: metadata?.name || location.id,
      }
    })
  }

  // Функция для расчета студентов на начало периода
  const calculateStudentsStartPeriod = (studentData) => {
    return (
      (studentData?.active_student || 0) +
      (studentData?.left_student || 0) -
      (studentData?.new_student || 0)
    )
  }

  // Конфиг колонок с правильными маппингами полей из бэка
  const cfg = {
    cols: [
      {
        name: 'Школа',
        group: true,
        formatter: (row) => row.location_name || row.id,
        classes: [st.color, st.fw500],
        align: 'left',
      },
      {
        name: 'Тип программы',
        formatter: (row) => {
          const programTypeId = row?.program_type
          const foundProgramType = catalog.program_types?.find(
            (skl) => skl.id === programTypeId,
          )
          return foundProgramType?.name || programTypeId
        },
        classes: [st.color, st.fz12, st.fw500],
        align: 'left',
      },
      {
        name: 'Студенты на начало периода',
        formatter: (row) => {
          return splitNumberDigits(calculateStudentsStartPeriod(row))
        },
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Новые студенты',
        formatter: (row, studentsStartPeriod) => {
          return formatNumberWithPercent(
            row.new_student || 0,
            studentsStartPeriod,
          )
        },
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Выбывшие студенты',
        formatter: (row, studentsStartPeriod) => {
          return formatNumberWithPercent(
            row.left_student || 0,
            studentsStartPeriod,
          )
        },
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Студенты на конец периода',
        formatter: (row, studentsStartPeriod) => {
          return formatNumberWithPercent(
            row.active_student || 0,
            studentsStartPeriod,
          )
        },
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Количество групп',
        formatter: (row) => splitNumberDigits(row.count_group || 0),
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Наполняемость групп в динамике',
        formatter: (row) => {
          const studentsStartPeriod = calculateStudentsStartPeriod(row)
          const countGroup = row.count_group || 1
          return splitNumberDigits(
            (studentsStartPeriod / countGroup).toFixed(2),
          )
        },
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Наполняемость групп на конец периода',
        formatter: (row) => {
          const activeStudents = row.active_student || 0
          const countGroup = row.count_group || 1
          return splitNumberDigits((activeStudents / countGroup).toFixed(2))
        },
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Наполняемость студентов по отработанным часам',
        formatter: (row) => {
          const studentHours = row.man_hours || 0
          const teacherHours = row.teacher?.man_hours || 0
          return calculateStudentCapacityByHours(studentHours, teacherHours)
        },
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Количество часов отработанных студентом, ак. ч.',
        formatter: (row) => splitNumberDigits(Math.floor(row.man_hours || 0)),
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Количество часов отработанных учителем, ак. ч.',
        formatter: (row) =>
          splitNumberDigits(Math.floor(row.teacher?.man_hours || 0)),
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Количество учителей',
        formatter: (row) => splitNumberDigits(row.teacher?.count || 0),
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Средняя загрузка учителя в ак. ч.',
        formatter: (row) =>
          splitNumberDigits((row.teacher?.average_load || 0).toFixed(2)),
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Продолжительность жизни студента, месяцев',
        formatter: (row) =>
          splitNumberDigits((row.study_length || 0).toFixed(2)),
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Продолжительность жизни студента, акк ч',
        formatter: (row) =>
          splitNumberDigits((row.study_length_hours || 0).toFixed(2)),
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Количество аудиторий',
        formatter: () => '', // Пустая ячейка в теле таблицы
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Площадь помещений, кв. м',
        formatter: () => '', // Пустая ячейка в теле таблицы
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Количество студентов на одну аудиторию (офлайн)',
        formatter: () => '', // Пустая ячейка в теле таблицы
        classes: [st.fz12],
        align: 'left',
      },
    ],
  }

  useEffect(() => {
    setIsLoading(true)
    superAxiosRequest({
      method: 'get',
      link: 'progress_report/profitability_programs',
    })
      .then(({ data }) => {
        setIsLoading(false)
        setCatalog(data)

        // Обогащаем данные локаций метаданными
        const enrichedLocations = enrichLocationsWithMetadata(
          data.result?.locations,
          data.locations,
        )

        const filteredLocations = enrichedLocations?.filter(
          (location) => location.program_types?.length > 0,
        )
        setData({ locations: filteredLocations, total: data.result?.total })
      })
      .catch(() => setIsLoading(false))
  }, [])

  useEffect(() => {
    if (filter && Object.keys(filter).length === 0) {
      return
    }
    setIsLoading(true)
    superAxiosRequest({
      method: 'get',
      link: 'progress_report/profitability_programs',
      params: {
        ...filter,
      },
    })
      .then(({ data }) => {
        const getDataWithCorrectProgramTypes = () => {
          // Обогащаем данные локаций метаданными
          const enrichedLocations = enrichLocationsWithMetadata(
            data.result.locations,
            data.locations,
          )

          const filteredLocations = enrichedLocations.filter(
            (location) => location.program_types?.length > 0,
          )
          if (filter.program_type) {
            const programTypeId = parseInt(filter.program_type, 10)
            return filteredLocations
              .map((location) => {
                const matchingProgramType = location.program_types.find(
                  (pt) => pt.program_type === programTypeId,
                )
                return matchingProgramType
                  ? { ...location, program_types: [matchingProgramType] }
                  : null
              })
              .filter(Boolean)
          }
          return filteredLocations
        }
        setData({
          locations: getDataWithCorrectProgramTypes(),
          total: data?.result?.total,
        })
        setIsLoading(false)
      })
      .catch(() => {
        setIsLoading(false)
      })
  }, [filter])

  useEffect(() => {
    return AsideFilterStore.unmountFilter
  }, [])

  useEffect(() => {
    const unlisten = history.listen((location) => {
      if (location.pathname !== '/reports/profitability-programs') {
        updateFilter({})
      }
    })
    return () => {
      unlisten()
    }
  }, [history, updateFilter])

  // Функция для получения текста фильтров
  const getFilterText = () => {
    const parts = []

    if (filter.location && filter.location.length > 0) {
      const locationNames = filter.location
        .map((id) => {
          const loc = catalog.locations?.find((l) => l.id === parseInt(id))
          return loc?.name || id
        })
        .join(', ')
      parts.push(`Локация - ${locationNames}`)
    }

    if (filter.date_from) {
      parts.push(`Дата начала - ${filter.date_from}`)
    }

    if (filter.date_until) {
      parts.push(`Дата окончания - ${filter.date_until}`)
    }

    if (filter.program_type && filter.program_type.length > 0) {
      const programNames = filter.program_type
        .map((id) => {
          const pt = catalog.program_types?.find((p) => p.id === parseInt(id))
          return pt?.name || id
        })
        .join(', ')
      parts.push(`Тип программы - ${programNames}`)
    }

    if (
      filter.online !== undefined &&
      filter.online !== null &&
      filter.online !== ''
    ) {
      const onlineMap = {
        0: 'Офлайн',
        1: 'Онлайн',
        2: 'Все группы',
      }
      parts.push(`Формат - ${onlineMap[filter.online] || filter.online}`)
    }

    if (filter.search) {
      parts.push(`Поиск - ${filter.search}`)
    }

    return parts.join('; ')
  }

  // Функция для экспорта Excel
  const downloadExcel = (data) => {
    import('xlsx-js-style').then((XLSX) => {
      // Заголовок отчета
      const reportTitle = 'Управленческий отчет'
      const filterText = getFilterText()

      // Создаем заголовки с правильным форматированием
      const headRows = [
        // Первая строка - название отчета (объединенная ячейка)
        [
          {
            v: reportTitle,
            s: {
              font: { bold: true, sz: 18, color: { rgb: '1A3C5E' } },
              alignment: { horizontal: 'left', vertical: 'center' },
            },
          },
        ],
        // Вторая строка - фильтры
        [
          {
            v: filterText || 'Фильтры не выбраны',
            s: {
              font: { sz: 11, italic: true, color: { rgb: '333333' } },
              alignment: { horizontal: 'left', vertical: 'center' },
            },
          },
        ],
        // Пустая строка
        [],
        // Заголовки колонок
        setCellParams(
          cfg.cols.map((col) => ({ v: col.name })),
          ExcelRowsParams.headerRowParam,
        ),
      ]

      let tableData = []
      let merges = []
      let wscols = cfg.cols.map(() => ({ wch: 20 }))
      let rows = [{ hpx: 75 }]
      let currentRow = 4 // Начинаем с 4 строки (заголовок + фильтры + пустая строка)

      // ВСЕГО ИТОГО (всегда показываем)
      if (data?.total) {
        const total = data.total
        const totalStudent = total.student || {}
        const totalTeacher = total.teacher || {}
        const totalStudentsStartPeriod =
          calculateStudentsStartPeriod(totalStudent)

        // Суммируем аудитории и площади по всем локациям
        let totalClassRooms = 0
        let totalSquareMeters = 0

        // Собираем данные для totals по учителям
        let totalTeacherHours = 0
        let totalTeacherCount = 0
        data.locations?.forEach((location) => {
          totalClassRooms += location.class_rooms || 0
          totalSquareMeters += location.square_meters || 0
          location.program_types?.forEach((pt) => {
            totalTeacherHours += pt.teacher?.man_hours || 0
            totalTeacherCount += pt.teacher?.count || 0
          })
        })

        // Расчет наполняемости для ВСЕГО ИТОГО
        const totalCountGroup = totalStudent.count_group || 1
        const totalFulfillDynamic = totalStudentsStartPeriod / totalCountGroup
        const totalFulfill =
          (totalStudent.active_student || 0) / totalCountGroup

        // Наполняемость по часам
        const totalStudentHours = totalStudent.man_hours || 0
        const totalCapacityByHours = calculateStudentCapacityByHours(
          totalStudentHours,
          totalTeacherHours,
        )

        // Средняя загрузка учителя
        const totalAvgTeacherLoad = calculateTeacherAverageLoad(
          totalTeacherHours,
          totalTeacherCount,
        )

        const totalRowData = [
          { v: 'ВСЕГО ИТОГО', ...ExcelRowsParams.footerRowParams.firstCell },
          { v: '' },
          { v: splitNumberDigits(totalStudentsStartPeriod) },
          {
            v: formatNumberWithPercent(
              totalStudent.new_student || 0,
              totalStudentsStartPeriod,
            ),
          },
          {
            v: formatNumberWithPercent(
              totalStudent.left_student || 0,
              totalStudentsStartPeriod,
            ),
          },
          {
            v: formatNumberWithPercent(
              totalStudent.active_student || 0,
              totalStudentsStartPeriod,
            ),
          },
          { v: splitNumberDigits(totalCountGroup) },
          { v: splitNumberDigits(totalFulfillDynamic.toFixed(2)) },
          { v: splitNumberDigits(totalFulfill.toFixed(2)) },
          { v: totalCapacityByHours },
          { v: splitNumberDigits(Math.floor(totalStudentHours)) },
          { v: splitNumberDigits(Math.floor(totalTeacherHours)) },
          { v: splitNumberDigits(totalTeacherCount) },
          { v: totalAvgTeacherLoad },
          // Используем значения напрямую с бэка
          { v: splitNumberDigits((totalStudent.study_length || 0).toFixed(2)) },
          {
            v: splitNumberDigits(
              (totalStudent.study_length_hours || 0).toFixed(2),
            ),
          },
          { v: splitNumberDigits(totalClassRooms) },
          { v: splitNumberDigits(totalSquareMeters) },
          {
            v: splitNumberDigits(
              (totalStudent.offline_student_per_classroom || 0).toFixed(2),
            ),
          },
        ]
        const formattedTotalRow = totalRowData.map((item, id) =>
          id === 0
            ? item
            : { ...item, ...ExcelRowsParams.footerRowParams.cell },
        )
        tableData.push(formattedTotalRow)
        currentRow++
      }

      // Данные по локациям и программам
      data?.locations?.forEach((location) => {
        const locationStudentsStartPeriod = calculateStudentsStartPeriod(
          location.total?.student,
        )
        const classRooms = location.class_rooms || 1
        const squareMeters = location.square_meters || 1

        if (location.program_types.length > 1) {
          merges.push({
            s: { r: currentRow, c: 0 },
            e: { r: currentRow + location.program_types.length - 1, c: 0 },
          })
        }

        location.program_types.forEach((program_type) => {
          const studentsStartPeriod = calculateStudentsStartPeriod(
            program_type.student,
          )

          const programTypeName =
            catalog.program_types?.find(
              (el) => el.id === program_type.program_type,
            )?.name || program_type.program_type

          const countGroup = program_type.student?.count_group || 1
          const fulfillDynamic = studentsStartPeriod / countGroup
          const fulfill =
            (program_type.student?.active_student || 0) / countGroup
          const capacityByHours = calculateStudentCapacityByHours(
            program_type.student?.man_hours || 0,
            program_type.teacher?.man_hours || 0,
          )

          let rowData = [
            { v: location.location_name || location.id },
            { v: programTypeName },
            { v: splitNumberDigits(studentsStartPeriod) },
            {
              v: formatNumberWithPercent(
                program_type.student?.new_student || 0,
                studentsStartPeriod,
              ),
            },
            {
              v: formatNumberWithPercent(
                program_type.student?.left_student || 0,
                studentsStartPeriod,
              ),
            },
            {
              v: formatNumberWithPercent(
                program_type.student?.active_student || 0,
                studentsStartPeriod,
              ),
            },
            { v: splitNumberDigits(countGroup) },
            { v: splitNumberDigits(fulfillDynamic.toFixed(2)) },
            { v: splitNumberDigits(fulfill.toFixed(2)) },
            { v: capacityByHours },
            {
              v: splitNumberDigits(
                Math.floor(program_type.student?.man_hours || 0),
              ),
            },
            {
              v: splitNumberDigits(
                Math.floor(program_type.teacher?.man_hours || 0),
              ),
            },
            { v: splitNumberDigits(program_type.teacher?.count || 0) },
            {
              v: splitNumberDigits(
                (program_type.teacher?.average_load || 0).toFixed(2),
              ),
            },
            {
              v: splitNumberDigits(
                (program_type.student?.study_length || 0).toFixed(2),
              ),
            },
            {
              v: splitNumberDigits(
                (program_type.student?.study_length_hours || 0).toFixed(2),
              ),
            },
            { v: '' }, // Пустая ячейка для "Количество аудиторий"
            { v: '' }, // Пустая ячейка для "Площадь помещений, кв. м"
            { v: '' }, // Пустая ячейка для "Количество студентов на одну аудиторию"
          ]
          rowData = setCellParams(rowData, ExcelRowsParams.tableRowParam.cell)
          tableData.push(rowData)
          currentRow++
        })

        // Итого по локации
        const locationTotal = location.total
        const locationStudentsStartPeriod2 = calculateStudentsStartPeriod(
          locationTotal?.student,
        )

        // Собираем данные для totals по учителям для локации
        let locationTeacherHours = 0
        let locationTeacherCount = 0
        location.program_types.forEach((pt) => {
          locationTeacherHours += pt.teacher?.man_hours || 0
          locationTeacherCount += pt.teacher?.count || 0
        })

        const locationCountGroup = locationTotal?.student?.count_group || 1
        const locationFulfillDynamic =
          locationStudentsStartPeriod2 / locationCountGroup
        const locationFulfill =
          (locationTotal?.student?.active_student || 0) / locationCountGroup

        // Наполняемость по часам для локации
        const locationStudentHours = locationTotal?.student?.man_hours || 0
        const locationCapacityByHours = calculateStudentCapacityByHours(
          locationStudentHours,
          locationTeacherHours,
        )

        // Средняя загрузка учителя для локации
        const locationAvgTeacherLoad = calculateTeacherAverageLoad(
          locationTeacherHours,
          locationTeacherCount,
        )

        const locationTotalRow = [
          { v: 'Итого', ...ExcelRowsParams.footerRowParams.firstCell },
          { v: '' },
          { v: splitNumberDigits(locationStudentsStartPeriod2) },
          {
            v: formatNumberWithPercent(
              locationTotal?.student?.new_student || 0,
              locationStudentsStartPeriod2,
            ),
          },
          {
            v: formatNumberWithPercent(
              locationTotal?.student?.left_student || 0,
              locationStudentsStartPeriod2,
            ),
          },
          {
            v: formatNumberWithPercent(
              locationTotal?.student?.active_student || 0,
              locationStudentsStartPeriod2,
            ),
          },
          { v: splitNumberDigits(locationCountGroup) },
          { v: splitNumberDigits(locationFulfillDynamic.toFixed(2)) },
          { v: splitNumberDigits(locationFulfill.toFixed(2)) },
          { v: locationCapacityByHours },
          {
            v: splitNumberDigits(
              Math.floor(locationTotal?.student?.man_hours || 0),
            ),
          },
          { v: splitNumberDigits(Math.floor(locationTeacherHours)) },
          { v: splitNumberDigits(locationTeacherCount) },
          { v: locationAvgTeacherLoad },
          // Используем значения напрямую с бэка
          {
            v: splitNumberDigits(
              (locationTotal?.student?.study_length || 0).toFixed(2),
            ),
          },
          {
            v: splitNumberDigits(
              (locationTotal?.student?.study_length_hours || 0).toFixed(2),
            ),
          },
          { v: classRooms },
          { v: squareMeters },
          {
            v: splitNumberDigits(
              (
                locationTotal?.student?.offline_student_per_classroom || 0
              ).toFixed(2),
            ),
          },
        ]
        const formattedLocationTotal = locationTotalRow.map((item, id) =>
          id === 0
            ? item
            : { ...item, ...ExcelRowsParams.footerRowParams.cell },
        )
        tableData.push(formattedLocationTotal)
        currentRow++
      })

      let wb = XLSX.utils.book_new()
      const ws = XLSX.utils.aoa_to_sheet([...headRows, ...tableData])
      ws['!cols'] = wscols
      ws['!merges'] = merges
      ws['!rows'] = rows
      XLSX.utils.book_append_sheet(wb, ws, 'Управленческий отчет')
      XLSX.writeFile(wb, 'Управленческий отчет.xlsx')
    })
  }

  const locations =
    data?.locations?.filter((e) => e.program_types?.length > 0) || []
  const isFilterEmpty = !filterTextValues.length

  // Вычисление общих итогов для ВСЕГО ИТОГО
  const calculateTotalTotals = () => {
    if (!data?.locations) return null

    let totalClassRooms = 0
    let totalSquareMeters = 0

    data.locations.forEach((location) => {
      totalClassRooms += location.class_rooms || 0
      totalSquareMeters += location.square_meters || 0
    })

    return {
      classRooms: totalClassRooms,
      squareMeters: totalSquareMeters,
    }
  }

  const totalTotals = calculateTotalTotals()

  // Функция для получения totals значений по учителям для ВСЕГО ИТОГО
  const getTotalTeacherTotals = () => {
    if (!data?.locations) return { totalHours: 0, totalCount: 0 }

    let totalHours = 0
    let totalCount = 0
    data.locations.forEach((location) => {
      location.program_types?.forEach((pt) => {
        totalHours += pt.teacher?.man_hours || 0
        totalCount += pt.teacher?.count || 0
      })
    })

    return {
      totalHours,
      totalCount,
    }
  }

  const totalTeacherTotals = getTotalTeacherTotals()

  return (
    <>
      <ContentHeader title='Управленческий отчет' />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          margin: '10px',
        }}
      >
        <SearchBooks
          onSearch={(text) => {
            updateFilter({ ...filter, search: text })
            changeTextValue('search', {
              key: 'search',
              label: 'Search',
              value: text,
            })
          }}
          onClear={() => {
            const { search, ...obj } = filter
            updateFilter(obj)
            setTextValues(
              filterTextValues.filter((field) => field.key !== 'search'),
            )
          }}
          translate={translate}
          placeholder={`Search...`}
          value={filter?.search || ''}
          isFilterEmpty={isFilterEmpty}
        />
        {!!filterTextValues.length && (
          <FilterTextValues
            onDelete={(key) => {
              const { [key]: _, ...obj } = filter
              updateFilter(obj)
            }}
          />
        )}
        {isOpenFilter && (
          <AsideFilter
            translate={translate}
            getFilterParams={updateFilter}
            config={filterCnf}
            filterValue={filter}
            onDropFilter={() => {
              updateFilter({})
              closeFilter()
            }}
          />
        )}
      </div>

      {isLoading ? (
        <Loader />
      ) : data?.locations ? (
        <>
          <div style={{ padding: '10px', marginBottom: '10px' }}>
            <ExportExcelButton data={data} downloadExcel={downloadExcel} />
          </div>
          <div className={st.tableWrapper} ref={refWrapper}>
            <table className={st.table}>
              <THead cols={cfg.cols} ref={refThead} />
              <tbody className={`${st.tbody} ${st.tbodyStriped}`}>
                {/* ВСЕГО ИТОГО по фильтрам - показываем всегда */}
                {data?.total &&
                  totalTotals &&
                  (() => {
                    const totalStudent = data.total.student || {}
                    const totalStudentsStartPeriod =
                      calculateStudentsStartPeriod(totalStudent)
                    const totalCountGroup = totalStudent.count_group || 1

                    const totalFulfillDynamic =
                      totalStudentsStartPeriod / totalCountGroup
                    const totalFulfill =
                      (totalStudent.active_student || 0) / totalCountGroup

                    const totalStudentHours = totalStudent.man_hours || 0
                    const totalCapacityByHours =
                      calculateStudentCapacityByHours(
                        totalStudentHours,
                        totalTeacherTotals.totalHours,
                      )

                    const totalAvgTeacherLoad = calculateTeacherAverageLoad(
                      totalTeacherTotals.totalHours,
                      totalTeacherTotals.totalCount,
                    )

                    return (
                      <tr className={st.tdTotalTR}>
                        <td
                          className={st.tdTotal}
                          style={{ textAlign: 'left' }}
                        >
                          ВСЕГО ИТОГО
                        </td>
                        <td
                          className={st.tdTotal}
                          style={{ textAlign: 'left' }}
                        />
                        <td
                          className={`${st.tdTotal}`}
                          style={{ textAlign: 'left' }}
                        >
                          {splitNumberDigits(totalStudentsStartPeriod)}
                        </td>
                        <td
                          className={`${st.tdTotal}`}
                          style={{ textAlign: 'left' }}
                        >
                          {formatNumberWithPercent(
                            totalStudent.new_student || 0,
                            totalStudentsStartPeriod,
                          )}
                        </td>
                        <td
                          className={`${st.tdTotal}`}
                          style={{ textAlign: 'left' }}
                        >
                          {formatNumberWithPercent(
                            totalStudent.left_student || 0,
                            totalStudentsStartPeriod,
                          )}
                        </td>
                        <td
                          className={`${st.tdTotal}`}
                          style={{ textAlign: 'left' }}
                        >
                          {formatNumberWithPercent(
                            totalStudent.active_student || 0,
                            totalStudentsStartPeriod,
                          )}
                        </td>
                        <td
                          className={`${st.tdTotal}`}
                          style={{ textAlign: 'left' }}
                        >
                          {splitNumberDigits(totalCountGroup)}
                        </td>
                        <td
                          className={`${st.tdTotal}`}
                          style={{ textAlign: 'left' }}
                        >
                          {splitNumberDigits(totalFulfillDynamic.toFixed(2))}
                        </td>
                        <td
                          className={`${st.tdTotal}`}
                          style={{ textAlign: 'left' }}
                        >
                          {splitNumberDigits(totalFulfill.toFixed(2))}
                        </td>
                        <td
                          className={`${st.tdTotal}`}
                          style={{ textAlign: 'left' }}
                        >
                          {totalCapacityByHours}
                        </td>
                        <td
                          className={`${st.tdTotal}`}
                          style={{ textAlign: 'left' }}
                        >
                          {splitNumberDigits(Math.floor(totalStudentHours))}
                        </td>
                        <td
                          className={`${st.tdTotal}`}
                          style={{ textAlign: 'left' }}
                        >
                          {splitNumberDigits(
                            Math.floor(totalTeacherTotals.totalHours),
                          )}
                        </td>
                        <td
                          className={`${st.tdTotal}`}
                          style={{ textAlign: 'left' }}
                        >
                          {splitNumberDigits(totalTeacherTotals.totalCount)}
                        </td>
                        <td
                          className={`${st.tdTotal}`}
                          style={{ textAlign: 'left' }}
                        >
                          {totalAvgTeacherLoad}
                        </td>
                        <td
                          className={`${st.tdTotal}`}
                          style={{ textAlign: 'left' }}
                        >
                          {/* Используем study_length напрямую с бэка */}
                          {splitNumberDigits(
                            (totalStudent.study_length || 0).toFixed(2),
                          )}
                        </td>
                        <td
                          className={`${st.tdTotal}`}
                          style={{ textAlign: 'left' }}
                        >
                          {/* Используем study_length_hours напрямую с бэка */}
                          {splitNumberDigits(
                            (totalStudent.study_length_hours || 0).toFixed(2),
                          )}
                        </td>
                        <td
                          className={`${st.tdTotal}`}
                          style={{ textAlign: 'left' }}
                        >
                          {splitNumberDigits(totalTotals.classRooms)}
                        </td>
                        <td
                          className={`${st.tdTotal}`}
                          style={{ textAlign: 'left' }}
                        >
                          {splitNumberDigits(totalTotals.squareMeters)}
                        </td>
                        <td
                          className={`${st.tdTotal}`}
                          style={{ textAlign: 'left' }}
                        >
                          {splitNumberDigits(
                            (
                              totalStudent.offline_student_per_classroom || 0
                            ).toFixed(2),
                          )}
                        </td>
                      </tr>
                    )
                  })()}

                {/* Данные по локациям */}
                {locations.map((location) => {
                  const locationStudentsStartPeriod =
                    calculateStudentsStartPeriod(location.total?.student)
                  const classRooms = location.class_rooms || 1
                  const squareMeters = location.square_meters || 1
                  const locationName = location.location_name || location.id

                  // Функция для получения totals значений по учителям для локации
                  const getLocationTeacherTotals = () => {
                    let totalHours = 0
                    let totalCount = 0
                    location.program_types?.forEach((pt) => {
                      totalHours += pt.teacher?.man_hours || 0
                      totalCount += pt.teacher?.count || 0
                    })
                    return {
                      totalHours,
                      totalCount,
                    }
                  }

                  const locationTeacherTotals = getLocationTeacherTotals()

                  return (
                    <Fragment key={location.id}>
                      {location.program_types.map((pt, index) => {
                        const studentData = pt.student || {}
                        const teacherData = pt.teacher || {}

                        const studentsStartPeriod =
                          calculateStudentsStartPeriod(studentData)
                        const countGroup = studentData.count_group || 1

                        const fulfillDynamic = studentsStartPeriod / countGroup
                        const fulfill =
                          (studentData.active_student || 0) / countGroup

                        const capacityByHours = calculateStudentCapacityByHours(
                          studentData.man_hours || 0,
                          teacherData.man_hours || 0,
                        )

                        const programTypeName =
                          catalog.program_types?.find(
                            (el) => el.id === pt.program_type,
                          )?.name || pt.program_type

                        return (
                          <tr
                            key={`${location.id}-${index}`}
                            className={index === 0 ? st.bdt : undefined}
                          >
                            {cfg.cols.map((col, idxCol) => {
                              if (col.group) {
                                if (index === 0)
                                  return (
                                    <td
                                      key={idxCol}
                                      rowSpan={location.program_types.length}
                                      className={getTdClassNames(col)}
                                      style={{ textAlign: 'left' }}
                                    >
                                      {locationName}
                                    </td>
                                  )
                                else return null
                              } else {
                                if (col.name === 'Тип программы') {
                                  return (
                                    <td
                                      key={idxCol}
                                      className={getTdClassNames(col)}
                                      style={{ textAlign: 'left' }}
                                    >
                                      {programTypeName}
                                    </td>
                                  )
                                }
                                if (col.name === 'Студенты на начало периода') {
                                  return (
                                    <td
                                      key={idxCol}
                                      className={getTdClassNames(col)}
                                      style={{ textAlign: 'left' }}
                                    >
                                      {splitNumberDigits(studentsStartPeriod)}
                                    </td>
                                  )
                                }
                                if (col.name === 'Новые студенты') {
                                  return (
                                    <td
                                      key={idxCol}
                                      className={getTdClassNames(col)}
                                      style={{ textAlign: 'left' }}
                                    >
                                      {formatNumberWithPercent(
                                        studentData.new_student || 0,
                                        studentsStartPeriod,
                                      )}
                                    </td>
                                  )
                                }
                                if (col.name === 'Выбывшие студенты') {
                                  return (
                                    <td
                                      key={idxCol}
                                      className={getTdClassNames(col)}
                                      style={{ textAlign: 'left' }}
                                    >
                                      {formatNumberWithPercent(
                                        studentData.left_student || 0,
                                        studentsStartPeriod,
                                      )}
                                    </td>
                                  )
                                }
                                if (col.name === 'Студенты на конец периода') {
                                  return (
                                    <td
                                      key={idxCol}
                                      className={getTdClassNames(col)}
                                      style={{ textAlign: 'left' }}
                                    >
                                      {formatNumberWithPercent(
                                        studentData.active_student || 0,
                                        studentsStartPeriod,
                                      )}
                                    </td>
                                  )
                                }
                                if (col.name === 'Количество групп') {
                                  return (
                                    <td
                                      key={idxCol}
                                      className={getTdClassNames(col)}
                                      style={{ textAlign: 'left' }}
                                    >
                                      {splitNumberDigits(countGroup)}
                                    </td>
                                  )
                                }
                                if (
                                  col.name === 'Наполняемость групп в динамике'
                                ) {
                                  return (
                                    <td
                                      key={idxCol}
                                      className={getTdClassNames(col)}
                                      style={{ textAlign: 'left' }}
                                    >
                                      {splitNumberDigits(
                                        fulfillDynamic.toFixed(2),
                                      )}
                                    </td>
                                  )
                                }
                                if (
                                  col.name ===
                                  'Наполняемость групп на конец периода'
                                ) {
                                  return (
                                    <td
                                      key={idxCol}
                                      className={getTdClassNames(col)}
                                      style={{ textAlign: 'left' }}
                                    >
                                      {splitNumberDigits(fulfill.toFixed(2))}
                                    </td>
                                  )
                                }
                                if (
                                  col.name ===
                                  'Наполняемость студентов по отработанным часам'
                                ) {
                                  return (
                                    <td
                                      key={idxCol}
                                      className={getTdClassNames(col)}
                                      style={{ textAlign: 'left' }}
                                    >
                                      {capacityByHours}
                                    </td>
                                  )
                                }
                                // Пустые ячейки для колонок "Количество аудиторий", "Площадь помещений, кв. м", "Количество студентов на одну аудиторию"
                                if (
                                  col.name === 'Количество аудиторий' ||
                                  col.name === 'Площадь помещений, кв. м' ||
                                  col.name ===
                                    'Количество студентов на одну аудиторию (офлайн)'
                                ) {
                                  return (
                                    <td
                                      key={idxCol}
                                      className={getTdClassNames(col)}
                                      style={{ textAlign: 'left' }}
                                    />
                                  )
                                }
                                if (
                                  col.name ===
                                  'Продолжительность жизни студента, акк ч'
                                ) {
                                  return (
                                    <td
                                      key={idxCol}
                                      className={getTdClassNames(col)}
                                      style={{ textAlign: 'left' }}
                                    >
                                      {splitNumberDigits(
                                        (
                                          studentData.study_length_hours || 0
                                        ).toFixed(2),
                                      )}
                                    </td>
                                  )
                                }
                                if (
                                  col.name ===
                                  'Количество часов отработанных учителем, ак. ч.'
                                ) {
                                  return (
                                    <td
                                      key={idxCol}
                                      className={getTdClassNames(col)}
                                      style={{ textAlign: 'left' }}
                                    >
                                      {splitNumberDigits(
                                        Math.floor(teacherData.man_hours || 0),
                                      )}
                                    </td>
                                  )
                                }
                                if (
                                  col.name ===
                                  'Средняя загрузка учителя в ак. ч.'
                                ) {
                                  return (
                                    <td
                                      key={idxCol}
                                      className={getTdClassNames(col)}
                                      style={{ textAlign: 'left' }}
                                    >
                                      {splitNumberDigits(
                                        (teacherData.average_load || 0).toFixed(
                                          2,
                                        ),
                                      )}
                                    </td>
                                  )
                                }
                                if (col.name === 'Количество учителей') {
                                  return (
                                    <td
                                      key={idxCol}
                                      className={getTdClassNames(col)}
                                      style={{ textAlign: 'left' }}
                                    >
                                      {splitNumberDigits(
                                        teacherData.count || 0,
                                      )}
                                    </td>
                                  )
                                }
                                return (
                                  <td
                                    key={idxCol}
                                    className={getTdClassNames(col)}
                                    style={{ textAlign: 'left' }}
                                  >
                                    {typeof col.formatter === 'function'
                                      ? col.formatter(
                                          studentData,
                                          studentsStartPeriod,
                                          location,
                                        )
                                      : studentData[col.dataField]}
                                  </td>
                                )
                              }
                            })}
                          </tr>
                        )
                      })}
                      {/* Итого по локации */}
                      {location.total &&
                        (() => {
                          const locationTotal = location.total
                          const locationStudentsStartPeriod2 =
                            calculateStudentsStartPeriod(locationTotal?.student)
                          const locationCountGroup =
                            locationTotal?.student?.count_group || 1
                          const locationFulfillDynamic =
                            locationStudentsStartPeriod2 / locationCountGroup
                          const locationFulfill =
                            (locationTotal?.student?.active_student || 0) /
                            locationCountGroup

                          const locationStudentHours =
                            locationTotal?.student?.man_hours || 0
                          const locationCapacityByHours =
                            calculateStudentCapacityByHours(
                              locationStudentHours,
                              locationTeacherTotals.totalHours,
                            )

                          const locationAvgTeacherLoad =
                            calculateTeacherAverageLoad(
                              locationTeacherTotals.totalHours,
                              locationTeacherTotals.totalCount,
                            )

                          return (
                            <tr>
                              <td
                                className={st.tdTotal}
                                style={{ textAlign: 'left' }}
                              >
                                Итого
                              </td>
                              <td
                                className={st.tdTotal}
                                style={{ textAlign: 'left' }}
                              />
                              <td
                                className={`${st.tdTotal}`}
                                style={{ textAlign: 'left' }}
                              >
                                {splitNumberDigits(
                                  locationStudentsStartPeriod2,
                                )}
                              </td>
                              <td
                                className={`${st.tdTotal}`}
                                style={{ textAlign: 'left' }}
                              >
                                {formatNumberWithPercent(
                                  locationTotal.student?.new_student || 0,
                                  locationStudentsStartPeriod2,
                                )}
                              </td>
                              <td
                                className={`${st.tdTotal}`}
                                style={{ textAlign: 'left' }}
                              >
                                {formatNumberWithPercent(
                                  locationTotal.student?.left_student || 0,
                                  locationStudentsStartPeriod2,
                                )}
                              </td>
                              <td
                                className={`${st.tdTotal}`}
                                style={{ textAlign: 'left' }}
                              >
                                {formatNumberWithPercent(
                                  locationTotal.student?.active_student || 0,
                                  locationStudentsStartPeriod2,
                                )}
                              </td>
                              <td
                                className={`${st.tdTotal}`}
                                style={{ textAlign: 'left' }}
                              >
                                {splitNumberDigits(locationCountGroup)}
                              </td>
                              <td
                                className={`${st.tdTotal}`}
                                style={{ textAlign: 'left' }}
                              >
                                {splitNumberDigits(
                                  locationFulfillDynamic.toFixed(2),
                                )}
                              </td>
                              <td
                                className={`${st.tdTotal}`}
                                style={{ textAlign: 'left' }}
                              >
                                {splitNumberDigits(locationFulfill.toFixed(2))}
                              </td>
                              <td
                                className={`${st.tdTotal}`}
                                style={{ textAlign: 'left' }}
                              >
                                {locationCapacityByHours}
                              </td>
                              <td
                                className={`${st.tdTotal}`}
                                style={{ textAlign: 'left' }}
                              >
                                {splitNumberDigits(
                                  Math.floor(
                                    locationTotal.student?.man_hours || 0,
                                  ),
                                )}
                              </td>
                              <td
                                className={`${st.tdTotal}`}
                                style={{ textAlign: 'left' }}
                              >
                                {splitNumberDigits(
                                  Math.floor(locationTeacherTotals.totalHours),
                                )}
                              </td>
                              <td
                                className={`${st.tdTotal}`}
                                style={{ textAlign: 'left' }}
                              >
                                {splitNumberDigits(
                                  locationTeacherTotals.totalCount,
                                )}
                              </td>
                              <td
                                className={`${st.tdTotal}`}
                                style={{ textAlign: 'left' }}
                              >
                                {locationAvgTeacherLoad}
                              </td>
                              <td
                                className={`${st.tdTotal}`}
                                style={{ textAlign: 'left' }}
                              >
                                {/* Используем study_length напрямую с бэка */}
                                {splitNumberDigits(
                                  (
                                    locationTotal.student?.study_length || 0
                                  ).toFixed(2),
                                )}
                              </td>
                              <td
                                className={`${st.tdTotal}`}
                                style={{ textAlign: 'left' }}
                              >
                                {/* Используем study_length_hours напрямую с бэка */}
                                {splitNumberDigits(
                                  (
                                    locationTotal.student?.study_length_hours ||
                                    0
                                  ).toFixed(2),
                                )}
                              </td>
                              <td
                                className={`${st.tdTotal}`}
                                style={{ textAlign: 'left' }}
                              >
                                {classRooms}
                              </td>
                              <td
                                className={`${st.tdTotal}`}
                                style={{ textAlign: 'left' }}
                              >
                                {squareMeters}
                              </td>
                              <td
                                className={`${st.tdTotal}`}
                                style={{ textAlign: 'left' }}
                              >
                                {splitNumberDigits(
                                  (
                                    locationTotal.student
                                      ?.offline_student_per_classroom || 0
                                  ).toFixed(2),
                                )}
                              </td>
                            </tr>
                          )
                        })()}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <h1 className={st.noData}>Нет данных для отображения!</h1>
      )}
    </>
  )
}

export default observer(ProfitabilityPrograms)
